"use client";

import { useState } from "react";
import { LIFE_AREA_KEYS, LIFE_AREA_META } from "@/lib/lifeAreas";
import { formatLocalDateInput } from "@/lib/utils";

export interface ScheduleResourceParams {
  date: string;
  startTime: string;
  durationMin: number;
  lifeArea: string | null;
}

interface Props {
  itemTitle: string;
  defaultDurationMin: number;
  defaultLifeArea: string | null;
  isBusy: boolean;
  onCancel: () => void;
  onSubmit: (params: ScheduleResourceParams) => void;
}

export default function ScheduleResourcePicker({
  itemTitle,
  defaultDurationMin,
  defaultLifeArea,
  isBusy,
  onCancel,
  onSubmit,
}: Props) {
  const today = formatLocalDateInput(new Date());
  const [date, setDate] = useState(today);
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    const minutes = now.getMinutes() < 30 ? 30 : 0;
    if (minutes === 0) now.setHours(now.getHours() + 1);
    now.setMinutes(minutes, 0, 0);
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  });
  const [durationMin, setDurationMin] = useState(defaultDurationMin);
  const [lifeArea, setLifeArea] = useState<string | null>(defaultLifeArea);

  const lifeAreaOptions: Array<{ value: string | null; label: string; emoji: string }> = [
    { value: null, label: "Unlinked", emoji: "" },
    ...LIFE_AREA_KEYS.map((key) => ({
      value: key,
      label: LIFE_AREA_META[key].label,
      emoji: LIFE_AREA_META[key].emoji,
    })),
  ];

  return (
    <div
      data-testid="library-schedule-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/25 backdrop-blur-[2px] px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="library-schedule-title"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-2 rounded-t-2xl bg-[#0EA5E9]" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <span className="rounded-full bg-blue/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-blue">
              Schedule
            </span>
            <button
              type="button"
              onClick={onCancel}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <h2 id="library-schedule-title" className="mt-3 text-lg font-semibold leading-snug text-text">
            {itemTitle}
          </h2>
          <p className="mt-1 text-xs text-muted">
            Pick a time slot. The block lands on the Plan board with this start, end, and life area.
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Date</span>
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/30 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Start</span>
              <input
                type="time"
                value={startTime}
                step={300}
                onChange={(event) => setStartTime(event.target.value)}
                className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/30 focus:outline-none"
              />
            </label>
            <label className="col-span-2 flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Duration (minutes)</span>
              <input
                type="number"
                min={5}
                max={480}
                step={5}
                value={durationMin}
                onChange={(event) => setDurationMin(Math.max(5, Number(event.target.value) || 0))}
                className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/30 focus:outline-none"
              />
            </label>
          </div>

          <div className="mt-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">Life area</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {lifeAreaOptions.map((opt) => {
                const isActive = lifeArea === opt.value;
                return (
                  <button
                    key={opt.value ?? "unlinked"}
                    type="button"
                    onClick={() => setLifeArea(opt.value)}
                    className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                      isActive ? "bg-forest-med text-white" : "border border-border bg-white text-text-light hover:border-forest-med/30"
                    }`}
                  >
                    {opt.emoji} {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => onSubmit({ date, startTime, durationMin, lifeArea })}
              disabled={isBusy || !date || !startTime || durationMin <= 0}
              // codex follow-up 2026-05-25 (LIBRARY-DECISION P1
              // cluster): the modal "Schedule" submit shared an
              // accessible-name with the Library card's "Schedule"
              // button behind the modal, so the strict UAT's
              // `getByRole("button", { name: "Schedule", exact: true })`
              // could land on the wrong target. Explicit aria-label
              // + testid make the submit unambiguous.
              aria-label={isBusy ? "Scheduling" : "Schedule library item"}
              data-testid="library-schedule-submit"
              className="flex-1 rounded-full bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
            >
              {isBusy ? "Scheduling..." : "Schedule"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isBusy}
              className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition-colors hover:border-forest-med/40 hover:text-forest-med disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
